import { useRef, type KeyboardEvent } from 'react'
import type { EquationsPrompt } from '../generators/equations.ts'

/** The system of equations (prompt area of the question card). */
export function EquationsSystem({ prompt }: { prompt: EquationsPrompt }) {
  return (
    <div className="equations-list">
      {prompt.equations.map((eq, i) => (
        <div key={i} className="equation">
          {eq}
        </div>
      ))}
    </div>
  )
}

interface EquationsInputsProps {
  prompt: EquationsPrompt
  /** Current text per letter; controlled by the parent. */
  values: Record<string, string>
  onChange: (letter: string, value: string) => void
  disabled?: boolean
  /** After submit: per-letter correctness colours the fields. */
  perLetterCorrect?: Record<string, boolean> | null
  /** Called when Enter is pressed on the last field (submits if complete). */
  onSubmitLast?: () => void
}

/**
 * Answer UI (PLAN-Z M4): one numeric input per letter with inputmode="numeric"
 * (raises the number pad) and next-field-on-enter, so the whole item is
 * answerable without dismissing the keyboard.
 */
export function EquationsInputs({
  prompt,
  values,
  onChange,
  disabled = false,
  perLetterCorrect = null,
  onSubmitLast,
}: EquationsInputsProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([])

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    const next = refs.current[index + 1]
    if (next) next.focus()
    else onSubmitLast?.()
  }

  return (
    <div className="equations-answer" role="group" aria-label="Letter values">
      {prompt.letters.map((letter, i) => {
        const cls = ['equation-field-input']
        if (perLetterCorrect) cls.push(perLetterCorrect[letter] ? 'correct' : 'wrong')
        return (
          <label key={letter} className="equation-field">
            <span className="equation-letter">{letter} =</span>
            <input
              ref={(el) => {
                refs.current[i] = el
              }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={2}
              autoComplete="off"
              className={cls.join(' ')}
              aria-label={`Value of ${letter}`}
              value={values[letter] ?? ''}
              disabled={disabled || perLetterCorrect !== null}
              onChange={(e) => onChange(letter, e.target.value.replace(/\D/g, '').slice(0, 2))}
              onKeyDown={handleKeyDown(i)}
            />
          </label>
        )
      })}
    </div>
  )
}
